import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, KeyRound, Lock, Mail, RotateCcw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store';
import AuthShell from '../../components/auth/AuthShell';
import { APP_ROUTES } from '../../constants';

const inputBase =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-cyan-600 focus:ring-2 focus:ring-cyan-100';

function LoginPage() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loginChallenge, setLoginChallenge] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, verifyLoginEmail, resendLoginEmailVerification, loading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionNoticeShownRef = useRef(false);
  const logoutReason = location.state?.reason;
  const noticeShown = location.state?.noticeShown;

  useEffect(() => {
    if (
      logoutReason === 'idle_timeout' &&
      !noticeShown &&
      !sessionNoticeShownRef.current
    ) {
      sessionNoticeShownRef.current = true;
      toast.error('Session expired after 30 minutes of inactivity.');
    }
  }, [logoutReason, noticeShown]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    const result = loginChallenge
      ? await verifyLoginEmail({
        email: loginChallenge.email,
        challengeId: loginChallenge.challengeId,
        code: verificationCode.trim()
      })
      : await login({
        email: formData.email.trim(),
        password: formData.password
      });

    if (!result.success) {
      toast.error(result.error || 'Unable to sign in with those credentials.');
      return;
    }

    if (result.requiresLoginEmailVerification) {
      setLoginChallenge({
        email: result.email || formData.email.trim(),
        challengeId: result.challengeId,
        expiresInMinutes: result.expiresInMinutes,
        maxAttempts: result.maxAttempts
      });
      setVerificationCode('');
      toast.success('Check your email for the admin sign-in code.');
      return;
    }

    toast.success('Signed in successfully.');
    navigate(APP_ROUTES.HOME, { replace: true });
  };

  const handleResendCode = async () => {
    if (!loginChallenge || loading) return;

    const result = await resendLoginEmailVerification({
      email: loginChallenge.email,
      challengeId: loginChallenge.challengeId
    });

    if (!result.success) {
      toast.error(result.error || 'Could not resend the sign-in code.');
      return;
    }

    setLoginChallenge({
      email: result.email || loginChallenge.email,
      challengeId: result.challengeId || loginChallenge.challengeId,
      expiresInMinutes: result.expiresInMinutes || loginChallenge.expiresInMinutes,
      maxAttempts: result.maxAttempts || loginChallenge.maxAttempts
    });
    setVerificationCode('');
    toast.success('A new admin sign-in code was sent.');
  };

  return (
    <AuthShell
      title="Sanspath Admin"
      subtitle=""
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {!loginChallenge ? (
          <>
            

            <div>
              <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700">
                Admin email
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`${inputBase} pl-10`}
                  placeholder="admin@sanspath.com"
                  autoFocus
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-700">
                Password
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`${inputBase} pl-10 pr-10`}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute right-3 top-3.5 text-slate-400 transition hover:text-slate-700"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-cyan-100 bg-cyan-50 px-4 py-3 text-sm text-slate-700">
              Enter the 6 digit code sent to <span className="font-semibold text-slate-950">{loginChallenge.email}</span>.
              <span className="mt-1 block text-xs text-slate-500">
                Maximum attempts: {loginChallenge.maxAttempts || 3}
              </span>
            </div>

            <div>
              <label htmlFor="verificationCode" className="mb-2 block text-sm font-medium text-slate-700">
                Admin sign-in code
              </label>
              <div className="relative">
                <KeyRound className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  id="verificationCode"
                  name="verificationCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  autoComplete="one-time-code"
                  value={verificationCode}
                  onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  className={`${inputBase} pl-10 tracking-[0.35em]`}
                  placeholder="000000"
                  autoFocus
                  required
                />
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || (loginChallenge && verificationCode.length !== 6)}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-cyan-700 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-cyan-800 focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'Signing in...' : loginChallenge ? 'Verify and sign in' : 'Sign in'}
          {!loading && <ArrowRight className="h-4 w-4" />}
        </button>

        {loginChallenge && (
          <div className="flex flex-col gap-3 text-center text-sm sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 font-medium text-cyan-700 transition hover:text-cyan-900 disabled:opacity-60"
            >
              <RotateCcw className="h-4 w-4" />
              Resend code
            </button>
            <button
              type="button"
              onClick={() => {
                setLoginChallenge(null);
                setVerificationCode('');
              }}
              className="font-medium text-slate-500 transition hover:text-slate-950"
            >
              Use a different account
            </button>
          </div>
        )}
      </form>
    </AuthShell>
  );
}

export default LoginPage;
